import {show, hide, select} from './tabs-core.js';
import {throttle} from './tabs-utils.js';

/**
 * Préparation de la limite des onglets basée sur la largeur du header.
 * @param {HTMLElement} tabs les onglets
 */
export function initEventsDropdown(tabs) {
  const dropdown = getDropdown(tabs);
  if (!dropdown) {
    return;
  }
  dropdown.addEventListener('keyup', handleKeyupDropdownButton(dropdown));
  dropdown.addEventListener('keydown', noGlobalScroll, {capture: true});
  dropdown.popoverTargetElement.addEventListener('keyup', handleKeyupDropdown(dropdown));
  dropdown.popoverTargetElement.addEventListener('click', handleClickDropdown(dropdown));
  dropdown.popoverTargetElement.addEventListener('keydown', noGlobalScroll, {capture: true});
}

/**
 * Préparation de la limite des onglets basée sur la largeur du header.
 * @param {HTMLElement} tabs les onglets
 */
export function prepareLimit(tabs) {
  const tabsHeader = tabs.querySelector(':scope > .js-tabs__header');
  const tabsList = tabsHeader.querySelector(':scope > .js-tabs__list');
  const dropdown = tabsList.querySelector(`[data-dropdown="${tabsList.dataset.scope}"]`);
  const dropdownMenu = document.createElement('div');
  dropdownMenu.classList.add('l-flex', 'l-flex-column');
  dropdown.popoverTargetElement.appendChild(dropdownMenu);
  createResizeObserver(tabsHeader, tabsList, dropdown, dropdownMenu);
  createMutationObserver(tabsHeader, tabsList, dropdown, dropdownMenu);
}

/**
 * Observe les modifications de taille de la barre d'onglets
 * @param {HTMLElement} tabsHeader conteneur sur lequel la largeur est testée
 * @param {HTMLElement} tabsList liste des onglets
 * @param {HTMLButtonElement} dropdown bouton dropdown
 * @param {HTMLElement} popoverTarget cible de rangement des onglets
 */
function createResizeObserver(tabsHeader, tabsList, dropdown, popoverTarget) {
  const resizeObserver = new ResizeObserver(() => {
    throttledAdapt(tabsHeader, tabsList, dropdown, popoverTarget);
  });
  resizeObserver.observe(tabsHeader);
  adapt(tabsHeader, tabsList, dropdown, popoverTarget);
}

/**
 * Observe les modifications de taille de la barre d'onglets
 * @param {HTMLElement} tabsHeader conteneur sur lequel la largeur est testée
 * @param {HTMLElement} tabsList liste des onglets
 * @param {HTMLButtonElement} dropdown bouton dropdown
 * @param {HTMLElement} popoverTarget cible de rangement des onglets
 */
function createMutationObserver(tabsHeader, tabsList, dropdown, popoverTarget) {
  const mutationObserver = new MutationObserver(() => {
    updateDropdown(tabsHeader, dropdown, popoverTarget);
  });
  mutationObserver.observe(popoverTarget, {subtree: true, attributes: true, attributeFilter: ['aria-selected']});
}

/**
 * Décide du titre à afficher pour le bouton « plus d'onglets»
 * @param {HTMLElement} tabsHeader conteneur sur lequel la largeur est testée
 * @param {HTMLButtonElement} dropdown bouton dropdown
 * @param {HTMLElement} popoverTarget cible de rangement des onglets
 */
function updateDropdown(tabsHeader, dropdown, popoverTarget) {
  const selectedEntry = popoverTarget.querySelector(':scope [aria-selected="true"]');
  const dropdownTitleElement = dropdown.querySelector(':scope .u-popover__title');
  if (selectedEntry) {
    if (!dropdownTitleElement.dataset.oldTitle) {
      dropdownTitleElement.dataset.oldTitle = dropdownTitleElement.innerHTML;
    }
    dropdownTitleElement.innerHTML = selectedEntry.querySelector('.js-tabs__label').innerHTML;
    dropdown.setAttribute('aria-selected', 'true');
    dropdown.setAttribute('tabindex', '0');
  } else {
    dropdown.setAttribute('aria-selected', 'false');
    dropdown.setAttribute('tabindex', '-1');
  }
  if (!selectedEntry && dropdownTitleElement.dataset.oldTitle) {
    dropdownTitleElement.innerHTML = dropdownTitleElement.dataset.oldTitle;
  }
}

/**
 * Adaptation des onglets si trop large
 * @param {HTMLElement} tabsHeader conteneur sur lequel la largeur est testée
 * @param {HTMLElement} tabsList liste des onglets
 * @param {HTMLButtonElement} dropdown bouton dropdown
 * @param {HTMLElement} popoverTarget cible de rangement des onglets
 */
function adapt(tabsHeader, tabsList, dropdown, popoverTarget) {
  updateDropdown(tabsHeader, dropdown, popoverTarget);
  popAllTabs(tabsList, popoverTarget);
  show(dropdown);
  while (isOverflown(tabsList, tabsHeader) && tabsList.querySelectorAll(':scope > .js-tabs__button').length > 0) {
    const toMove = Array.from(tabsList.querySelectorAll(':scope > .js-tabs__button')).at(-1);
    toMove.removeAttribute('tabindex');
    popoverTarget.prepend(toMove);
  }
  if (popoverTarget.querySelectorAll(':scope > .js-tabs__button').length === 0) {
    hide(dropdown);
  }
  tabsList.append(dropdown);
  updateDropdown(tabsHeader, dropdown, popoverTarget);
}

/**
 * Version limitée de `adapt`.
 * @type {Function}
 */
const throttledAdapt = throttle(adapt, 50);

/**
 * Sort tous les boutons du popover s'arrête si l'un d'entre eux est sélectionné.
 * @param {HTMLElement} tabsList liste des onglets
 * @param {HTMLElement} popoverTarget cible de rangement des onglets
 */
function popAllTabs(tabsList, popoverTarget) {
  let nextTab = popoverTarget.querySelector('.js-tabs__button');
  while (nextTab) {
    tabsList.append(nextTab);
    nextTab = popoverTarget.querySelector('.js-tabs__button');
  }
}

/**
 * Vérifie si un élément est plus large que l'espace qui lui ai réversé
 * @param {HTMLElement} element élément sur lequel la largeur est testée
 * @param {HTMLElement} parent conteneur dans lequel la largeur est testée
 * @return {boolean} true si le flex du conteneur a déclenché le wrap et qu'un élément est plus «bas» que le conteneur
 */
function isOverflown(element, parent) {
  const availableWidth = parent.getBoundingClientRect().width - Array.from(parent.children)
    .filter(i => i !== element)
    .map(i => i.getBoundingClientRect().width)
    .reduce((a,c) => a+c, 0);

  return element.getBoundingClientRect().width > availableWidth - 50;
}

/**
 * Fabrique de gestionnaire d'événements clavier pour les items du dropdown.
 * @param {HTMLButtonElement} dropdown bouton du menu
 * @return {EventListener} le gestionnaire
 */
function handleKeyupDropdown(dropdown) {
  const keysAction = {
    ArrowDown: focusNextItemDropdown,
    ArrowUp: focusPreviousItemDropdown,
    Home: openFirstItemDropdown,
    End: openLastItemDropdown,
  };

  /**
   * Gestionnaire d'événement clavier pour les dropdowns.
   * @param {KeyboardEvent} event event
   */
  return function handleKeyupEventHandler(event) {
    event.stopPropagation();
    const buttons = Array.from(dropdown.popoverTargetElement.querySelectorAll('.js-tabs__button'));
    const currentButtonIndex = buttons.findIndex((b) => b.id === event.target.id);
    if (Object.hasOwn(keysAction, event.key)) {
      keysAction[event.key]({currentButtonIndex, event, buttons, dropdown});
    }
  };
}

/**
 * Active l'onglet précédent.
 */
function focusPreviousTab({dropdown}) {
  dropdown.popoverTargetElement.hidePopover();
  const tabButtons = dropdown?.closest('.js-tabs__list')?.querySelectorAll(':scope > .js-tabs__button--focusable:not(.tabs__limit-dropdown)');
  if (tabButtons && tabButtons.length > 0) {
    tabButtons.item(tabButtons.length - 1).focus();
  }
}

/**
 * Fabrique de gestionnaire d'événements clavier pour les items du dropdown.
 * @param {HTMLButtonElement} dropdown bouton du menu
 * @return {EventListener} le gestionnaire
 */
function handleKeyupDropdownButton(dropdown) {

  /**
   * Gestionnaire d'événement clavier pour les dropdowns.
   * @param {KeyboardEvent} event event
   */
  return function handleKeyupEventHandler(event) {
    event.stopPropagation();
    event.preventDefault();
    const buttons = Array.from(dropdown.popoverTargetElement.querySelectorAll('.js-tabs__button'));
    if (event.key === 'ArrowDown') {
      openFirstItemDropdown({buttons});
    } else if (event.key === 'ArrowLeft') {
      focusPreviousTab({dropdown});
    }
  };
}
/**
 * Empêche le scroll lorsque l'utilisateur utilise les flèches haut et bas sur le dropdown.
 * @param {KeyboardEvent} event event
 */
function noGlobalScroll(event) {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.stopImmediatePropagation();
    event.preventDefault();
  }
}

/**
 * Sélectionne l'onglet du popover.
 * @param {HTMLButtonElement} dropdown le bouton de popover
 * @param {Event} event l'événement déclencheur
 */
function selectTab({dropdown, event}) {
  const currentTab = event.target.closest('.js-tabs__button');
  select(currentTab);
  dropdown.setAttribute('aria-selected', 'true');
  dropdown.setAttribute('tabindex', '0');
  dropdown.popoverTargetElement.hidePopover();
}

/**
 * Fabrique de gestionnaire d'événements de click pour le dropdown.
 * @param {HTMLButtonElement} dropdown bouton du menu
 * @return {EventListener} le gestionnaire
 */
function handleClickDropdown(dropdown) {

  /**
   * Gestionnaire d'événement click pour le dropdown.
   * @param {MouseEvent} event event
   */
  return function handleClickDropdownHandler(event) {
    selectTab({dropdown, event});
  };
}

/**
 * Active l'élément suivant dans le dropdown.
 * @param {Number} currentButtonIndex index du menu courrant
 * @param {HTMLButtonElement[]} buttons tableau des buttons du menu
 */
function focusNextItemDropdown({currentButtonIndex, buttons}) {
  if (currentButtonIndex < buttons.length) {
    buttons[currentButtonIndex + 1]?.focus();
  }
}

/**
 * Active l'élément précédent dans le dropdown.
 * @param {Number} currentButtonIndex index du menu courrant
 * @param {HTMLButtonElement[]} buttons tableau des buttons du menu
 */
function focusPreviousItemDropdown({currentButtonIndex, buttons}) {
  if (currentButtonIndex > 0) {
    buttons[currentButtonIndex - 1]?.focus();
  }
}

/**
 * Active le premier élément dans le dropdown.
 * @param {HTMLButtonElement[]} buttons tableau des buttons du menu
 */
function openFirstItemDropdown({buttons}) {
  buttons[0]?.focus();
}

/**
 * Active le dernier élément dans le dropdown.
 * @param {HTMLButtonElement[]} buttons tableau des buttons du menu
 */
function openLastItemDropdown({buttons}) {
  buttons[buttons.length - 1]?.focus();
}

/**
 * Récupère le dropdown si présent.
 * @param {HTMLElement} tabs
 * @return {HTMLButtonElement} dropdown
 */
export function getDropdown(tabs) {
  return tabs.querySelector(`[data-dropdown="${tabs.dataset.key}"]`);
}
